import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { 
  Stethoscope, 
  Languages, 
  FileCheck, 
  Plane,
  Briefcase,
  GraduationCap,
  CheckCircle,
  ArrowRight,
  Globe,
  AlertCircle,
  MessageSquare,
  User,
  UserCheck,
  Volume2,
  BookOpen,
  Heart,
  Scale,
  Pill,
  ShieldCheck,
  Building2,
  Award,
  Users
} from 'lucide-react';

const HealthcareCareerPage = () => {
  const [careerPath, setCareerPath] = useState('nurse'); // 'nurse' or 'doctor'
  const [playingAudio, setPlayingAudio] = useState(null);

  // Text-to-speech function
  const speakGerman = (text, id) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.rate = 0.9;
      setPlayingAudio(id);
      utterance.onend = () => setPlayingAudio(null);
      window.speechSynthesis.speak(utterance);
    }
  };

  // Nurse phrases
  const nursePhrases = [
    { de: 'Sie dürfen in der 3 kurz Platz nehmen', en: 'You can take a seat in room 3' },
    { de: 'Ich helfe Ihnen beim Aufstehen', en: 'I will help you get up' },
    { de: 'Möchten Sie etwas trinken?', en: 'Would you like something to drink?' },
    { de: 'Ich messe jetzt Ihren Blutdruck', en: 'I will now measure your blood pressure' },
    { de: 'Haben Sie Schmerzen?', en: 'Are you in pain?' },
    { de: 'Der Arzt kommt gleich', en: 'The doctor will come soon' },
    { de: 'Ich bringe Ihnen Ihre Medikamente', en: 'I will bring you your medications' },
    { de: 'Brauchen Sie Hilfe beim Essen?', en: 'Do you need help eating?' },
  ];

  // Doctor phrases
  const doctorPhrases = [
    { de: 'Guten Tag', en: 'Good day' },
    { de: 'Mein Name ist...', en: 'My name is...' },
    { de: 'Ich bin Dr. ...', en: 'I am Doctor...' },
    { de: 'Sprechen Sie Englisch/Deutsch?', en: 'Do you speak English/German?' },
    { de: 'Verstehen Sie, was ich meine?', en: 'Do you understand what I mean?' },
    { de: 'Ich habe Sie nicht verstanden', en: 'I did not understand you' },
    { de: 'Könnten Sie das wiederholen?', en: 'Could you repeat that?' },
    { de: 'Entschuldigen Sie bitte', en: 'I am sorry' },
    { de: 'Ich möchte Sie gerne befragen', en: 'I would like to ask you about...' },
    { de: 'Dürfte ich Sie einmal untersuchen?', en: 'Could I examine you?' },
    { de: 'Haben Sie noch Fragen?', en: 'Do you have other questions?' },
    { de: 'Benötigen Sie Hilfe?', en: 'Do you need help?' },
    { de: 'Die Toilette ist hier', en: 'The toilet is here' },
    { de: 'Bis später', en: 'See you later' },
    { de: 'Gute Besserung wünsche ich Ihnen', en: 'I wish you a great recovery' },
    { de: 'Unterschreiben Sie bitte hier', en: 'Please sign here' },
    { de: 'Schönen Tag wünsche ich Ihnen', en: 'I wish you a great day' },
    { de: 'Wer ist Ihr Hausarzt?', en: 'Who is your family doctor?' },
    { de: 'Haben Sie Allergien?', en: 'Do you have any allergies?' },
    { de: 'Nehmen Sie Medikamente ein?', en: 'Are you taking any medications?' },
    { de: 'Haben Sie Beschwerden?', en: 'Do you have physical complaints?' },
    { de: 'Wurden Sie diagnostiziert?', en: 'Have you been diagnosed?' },
    { de: 'Rauchen Sie?', en: 'Do you smoke?' },
    { de: 'Welche Blutgruppe haben Sie?', en: 'Which blood group do you have?' },
    { de: 'Arbeiten Sie im Moment?', en: 'Are you currently working?' },
    { de: 'Wie viele Stunden schlafen Sie in der Nacht?', en: 'How many hours do you sleep at night?' },
    { de: 'Öffnen Sie die Augen!', en: 'Open your eyes!' },
    { de: 'Wie viele Finger sehen Sie?', en: 'How many fingers do you see?' },
    { de: 'Machen Sie ganz groß einmal A', en: 'Open your mouth and make a huge A' }
  ];

  const medicalVocabulary = [
    // Symptoms & Conditions
    { de: 'Die Verdauung', en: 'Digestion', category: 'symptoms' },
    { de: 'Der Stuhlgang', en: 'Stool', category: 'symptoms' },
    { de: 'Das Erbrechen', en: 'Vomit, puke', category: 'symptoms' },
    { de: 'Der Bauchkrampf', en: 'Abdominal cramps', category: 'symptoms' },
    { de: 'Die Magenschmerzen', en: 'Stomach cramps', category: 'symptoms' },
    { de: 'Die Periode', en: 'Period (informal: die Regel, die Tage)', category: 'symptoms' },
    { de: 'Der Schmerz', en: 'Pain', category: 'symptoms' },
    { de: 'Heuschnupfen', en: 'Hay fever', category: 'symptoms' },
    { de: 'Das Nasenbluten', en: 'Nose bleed', category: 'symptoms' },
    { de: 'Die Atemnot', en: 'Shortness of breath', category: 'symptoms' },
    { de: 'Der Herzfehler', en: 'Heart disease', category: 'symptoms' },
    { de: 'Die Sehstörung', en: 'Visual impairment', category: 'symptoms' },
    { de: 'Der Krebs', en: 'Cancer', category: 'symptoms' },
    { de: 'Die Tuberkulose', en: 'Tuberculosis', category: 'symptoms' },
    { de: 'Chronisch', en: 'Chronic', category: 'symptoms' },
    { de: 'Der Schwindel', en: 'Vertigo', category: 'symptoms' },
    { de: 'Schwindelig', en: 'Dizzy', category: 'symptoms' },
    { de: 'Die Hörstörung', en: 'Hearing impairment', category: 'symptoms' },
    { de: 'Die Sprachstörung', en: 'Speech disorders', category: 'symptoms' },
    { de: 'Die Inkontinenz', en: 'Incontinence', category: 'symptoms' },
    { de: 'Die Muskelkrämpfe', en: 'Muscle spasms', category: 'symptoms' },
    { de: 'Die Überdosis', en: 'Overdose', category: 'symptoms' },
    { de: 'Die Krampfanfälle', en: 'Seizures', category: 'symptoms' },
    { de: 'Die Halluzination', en: 'Hallucination', category: 'symptoms' },
    { de: 'Die Suizidgedanken', en: 'Suicidal thoughts', category: 'symptoms' },
    { de: 'Warzen', en: 'Verruca', category: 'symptoms' },
    { de: 'Die Skoliose', en: 'Scoliosis', category: 'symptoms' },
    { de: 'Die Kyphose', en: 'Kyphosis', category: 'symptoms' },
    { de: 'Die Lordose', en: 'Lordosis', category: 'symptoms' },
    
    // Medications & Treatments
    { de: 'Die Drogen', en: 'Drugs', category: 'medication' },
    { de: 'Die Medikamente', en: 'Medications', category: 'medication' },
    { de: 'Die Arzneimittel', en: 'Pharmaceuticals', category: 'medication' },
    { de: 'Die Ibu', en: 'Ibuprofen (informal)', category: 'medication' },
    { de: 'Das Zäpfchen', en: 'Suppository', category: 'medication' },
    { de: 'Das Insulin', en: 'Insulin', category: 'medication' },
    { de: 'Die Nebenwirkungen', en: 'Side effects', category: 'medication' },
    { de: 'Das Pflaster', en: 'Patch', category: 'medication' },
    { de: 'Die Spritze', en: 'Injection', category: 'medication' },
    { de: 'Die Chemotherapie', en: 'Chemotherapy (informal: Chemo)', category: 'medication' },
    { de: 'Die Physiotherapie', en: 'Physiotherapy', category: 'medication' },
    
    // Mental Health
    { de: 'ADHS', en: 'ADHD (Aufmerksamkeitsdefizit-Hyperaktivitätsstörung)', category: 'mental' },
    { de: 'Autismus', en: 'Autism', category: 'mental' },
    { de: 'Die bipolare Störung', en: 'Bipolar disorder', category: 'mental' },
    
    // Body Parts & Anatomy
    { de: 'Die Achselhöhle', en: 'Armpit', category: 'anatomy' },
    { de: 'Das Trommelfell', en: 'Eardrum', category: 'anatomy' },
    { de: 'Die Prostata', en: 'Prostate', category: 'anatomy' },
    { de: 'Die Gehirnzellen', en: 'Brain cells', category: 'anatomy' },
    { de: 'Die Schläfe', en: 'Temple', category: 'anatomy' },
    { de: 'Die Stirn', en: 'Forehead', category: 'anatomy' },
    { de: 'Die Pupille', en: 'Pupil', category: 'anatomy' },
    { de: 'Die Mundhöhle', en: 'Oral cavity', category: 'anatomy' },
    { de: 'Die Speicheldrüse', en: 'Salivary gland', category: 'anatomy' },
    { de: 'Der Gaumen', en: 'Palate', category: 'anatomy' },
    { de: 'Die Kehle', en: 'Throat', category: 'anatomy' },
    { de: 'Die Herzklappe', en: 'Cardiac valve', category: 'anatomy' },
    { de: 'Das After', en: 'Anus', category: 'anatomy' },
    { de: 'Die Milz', en: 'Spleen', category: 'anatomy' },
    { de: 'Die Geschlechtsorgane', en: 'Genitals', category: 'anatomy' },
    
    // Reproductive & Sexual Health
    { de: 'Der Geschlechtsverkehr', en: 'Intercourse', category: 'reproductive' },
    { de: 'Der Ausfluss', en: 'Vaginal discharge', category: 'reproductive' },
    { de: 'Die Schwangerschaft', en: 'Pregnancy', category: 'reproductive' },
    { de: 'Die Geburt', en: 'Birth', category: 'reproductive' },
    { de: 'Der Schwangerschaftstest', en: 'Pregnancy test', category: 'reproductive' },
    { de: 'Das Sperma', en: 'Sperm', category: 'reproductive' },
    
    // Lab & Diagnostics
    { de: 'Der Urin', en: 'Urine', category: 'lab' },
    { de: 'Die Motorik', en: 'Motor function', category: 'lab' },
    { de: 'Die Atemfrequenz', en: 'Respiratory rate', category: 'lab' },
    { de: 'Die Wunde', en: 'Wound', category: 'lab' },
    { de: 'Die Diagnose', en: 'Diagnosis', category: 'lab' },
    { de: 'Das Labor', en: 'Laboratory', category: 'lab' },
    { de: 'Die Blutkörperchen', en: 'Blood cells', category: 'lab' },
    { de: 'Der PH-Wert', en: 'pH balance', category: 'lab' },
    { de: 'Die Elektrolyte', en: 'Electrolytes', category: 'lab' },
    { de: 'Die Blutgerinnung', en: 'Clotting', category: 'lab' },
    { de: 'Die Antikörper', en: 'Antibody', category: 'lab' },
    { de: 'Die Aminosäuren', en: 'Amino acids', category: 'lab' },
    { de: 'Das Röntgenbild', en: 'X-Ray image', category: 'lab' },
    { de: 'Die Blutdruckmessung', en: 'Blood pressure measurement', category: 'lab' },
    { de: 'Die Untersuchung', en: 'Examination', category: 'lab' },
    { de: 'Die Gallensäure', en: 'Bile acids', category: 'lab' },
    { de: 'Der Eiter', en: 'Pus', category: 'lab' },
    
    // Hospital & Equipment
    { de: 'Die Notaufnahme', en: 'Emergency room', category: 'hospital' },
    { de: 'Der Chefarzt', en: 'Chief physician', category: 'hospital' },
    { de: 'Der Krankenpfleger', en: 'Nurse (neutral)', category: 'hospital' },
    { de: 'Die Kardiologie', en: 'Cardiology', category: 'hospital' },
    { de: 'Der Orthopäde', en: 'Orthopedist', category: 'hospital' },
    { de: 'Angehörige', en: 'Relatives', category: 'hospital' },
    { de: 'Die Infusionsflasche', en: 'Infusion bottle', category: 'hospital' },
    { de: 'Der Katheter', en: 'Catheter', category: 'hospital' },
    { de: 'Die Naht', en: 'Suture', category: 'hospital' },
    { de: 'Das Skalpell', en: 'Scalpel', category: 'hospital' },
    { de: 'Der Mundspiegel', en: 'Stomatoscope', category: 'hospital' },
    { de: 'Das Krankengeld', en: 'Sick pay', category: 'hospital' },
    
    // Questions
    { de: 'Hören Sie gut?', en: 'Do you hear well?', category: 'questions' },
    { de: 'Können Sie sich an etwas erinnern?', en: 'Can you remember something?', category: 'questions' },
  ];

  const bodyParts = [
    { de: 'Der Kopf', en: 'Head', emoji: '👤' },
    { de: 'Das Herz', en: 'Heart', emoji: '❤️' },
    { de: 'Der Brustkorb', en: 'Thorax', emoji: '🫁' },
    { de: 'Die Wirbelsäule', en: 'Spine', emoji: '🦴' },
    { de: 'Das Gesäß', en: 'Gluteal', emoji: '🍑' },
    { de: 'Das Skelett', en: 'Skeleton', emoji: '💀' },
    { de: 'Der Arm', en: 'Arm', emoji: '💪' },
    { de: 'Der Oberschenkel', en: 'Upper thigh', emoji: '🦵' },
    { de: 'Der Unterschenkel', en: 'Shank', emoji: '🦶' },
    { de: 'Die Schilddrüse', en: 'Thyroid', emoji: '🦋' },
    { de: 'Die Niere', en: 'Kidney', emoji: '🫘' },
    { de: 'Die Leber', en: 'Liver', emoji: '🫀' },
    { de: 'Das Gehirn', en: 'Brain', emoji: '🧠' },
    { de: 'Das Auge', en: 'Eye', emoji: '👁️' },
    { de: 'Das Ohr', en: 'Ear', emoji: '👂' },
    { de: 'Die Nase', en: 'Nose', emoji: '👃' },
    { de: 'Der Mund', en: 'Mouth', emoji: '👄' },
    { de: 'Die Zunge', en: 'Tongue', emoji: '👅' },
  ];

  const vocabCategories = [
    { id: 'symptoms', name: 'Symptoms & Conditions', icon: '🤒' },
    { id: 'medication', name: 'Medications & Treatments', icon: '💊' },
    { id: 'mental', name: 'Mental Health', icon: '🧠' },
    { id: 'anatomy', name: 'Body Parts & Anatomy', icon: '🫀' },
    { id: 'reproductive', name: 'Reproductive Health', icon: '👶' },
    { id: 'lab', name: 'Lab & Diagnostics', icon: '🔬' },
    { id: 'hospital', name: 'Hospital & Equipment', icon: '🏥' },
    { id: 'questions', name: 'Useful Questions', icon: '❓' },
  ];

  const [activeVocabCategory, setActiveVocabCategory] = useState('symptoms');

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
      <Navbar />

      {/* Hero Section with Career Selector */}
      <section className="py-12 md:py-16 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-100/30 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Stethoscope className="w-4 h-4" />
              Career Path
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-2">
              Healthcare & Nursing
            </h1>
            <p className="text-xl text-babyblue-600 mb-8">Gesundheit & Pflege</p>

            {/* Career Path Selector */}
            <div className="bg-white rounded-2xl p-2 inline-flex gap-2 shadow-lg border border-babyblue-200">
              <button
                onClick={() => setCareerPath('nurse')}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all ${
                  careerPath === 'nurse'
                    ? 'bg-gradient-to-r from-teal-500 to-teal-400 text-white shadow-md'
                    : 'bg-transparent text-babyblue-600 hover:bg-babyblue-50'
                }`}
              >
                <Users className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-lg">Nurse / Caregiver</div>
                  <div className={`text-xs ${careerPath === 'nurse' ? 'text-teal-100' : 'text-babyblue-400'}`}>
                    Krankenpfleger / Altenpfleger
                  </div>
                </div>
              </button>
              <button
                onClick={() => setCareerPath('doctor')}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all ${
                  careerPath === 'doctor'
                    ? 'bg-gradient-to-r from-primary to-blue-400 text-white shadow-md'
                    : 'bg-transparent text-babyblue-600 hover:bg-babyblue-50'
                }`}
              >
                <Stethoscope className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-lg">Doctor</div>
                  <div className={`text-xs ${careerPath === 'doctor' ? 'text-blue-100' : 'text-babyblue-400'}`}>
                    Arzt / Ärztin
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Introduction Card */}
          <div className={`rounded-2xl border p-6 shadow-lg transition-all ${
            careerPath === 'nurse' 
              ? 'bg-gradient-to-br from-teal-50 to-white border-teal-200' 
              : 'bg-gradient-to-br from-blue-50 to-white border-blue-200'
          }`}>
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                careerPath === 'nurse' ? 'bg-teal-100' : 'bg-blue-100'
              }`}>
                {careerPath === 'nurse' 
                  ? <Users className="w-7 h-7 text-teal-600" />
                  : <Stethoscope className="w-7 h-7 text-blue-600" />
                }
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">
                  {careerPath === 'nurse' ? 'Work as a Nurse or Caregiver' : 'Work as a Doctor'}
                </h2>
                <p className="text-babyblue-600 leading-relaxed">
                  {careerPath === 'nurse' 
                    ? <>If you want to practice a healthcare job in Germany, such as <strong className="text-babyblue-800">Kranken- oder Altenpfleger</strong> (nurse or elderly care worker), you must go through a step-by-step process because nursing is a <strong className="text-babyblue-800">regulated work field</strong> in Germany.</>
                    : <>Doctors who gained their degree abroad can work in Germany, but must complete the <strong className="text-babyblue-800">recognition process</strong> and pass the <strong className="text-babyblue-800">FSP-Prüfung</strong> (Medical Language Exam) to obtain their Approbation.</>
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== NURSE CONTENT ============== */}
      {careerPath === 'nurse' && (
        <>
          {/* Nurse Steps */}
          <section className="py-12 px-6 bg-white">
            <div className="container mx-auto max-w-4xl">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-8 text-center">
                Steps to Work as a Nurse in Germany
              </h2>

              <div className="space-y-4">
                {/* Step 1: Language */}
                <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Languages className="w-5 h-5 text-teal-600" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Language Level</h3>
                        <span className="text-babyblue-500 text-sm">(Sprachniveau)</span>
                      </div>
                      <p className="text-babyblue-600 mb-3">Communication is key in healthcare. <strong>All federal states require B2 level.</strong></p>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-white px-3 py-1 rounded-full text-sm border border-teal-200">
                          <strong className="text-teal-600">B1</strong> - Visa requirement
                        </span>
                        <span className="bg-white px-3 py-1 rounded-full text-sm border border-teal-200">
                          <strong className="text-teal-600">B2</strong> - Full nursing license
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 2: Recognition */}
                <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck className="w-5 h-5 text-teal-600" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Professional Recognition</h3>
                        <span className="text-babyblue-500 text-sm">(Berufsanerkennung)</span>
                      </div>
                      <p className="text-babyblue-600 mb-3">Submit your nursing diploma to the <strong>Anerkennungsstelle</strong> in your chosen state.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <p className="text-green-800 text-sm"><strong>EU/EEA:</strong> Usually automatic recognition</p>
                        </div>
                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                          <p className="text-amber-800 text-sm"><strong>Non-EU:</strong> May need to complete additional training</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Visa */}
                <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Plane className="w-5 h-5 text-teal-600" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Work Visa</h3>
                        <span className="text-babyblue-500 text-sm">(Arbeitsvisum)</span>
                      </div>
                      <p className="text-babyblue-600">Apply for a work visa with your B1 certificate. You can upgrade to full license once you reach B2.</p>
                    </div>
                  </div>
                </div>

                {/* Step 4: Start Working */}
                <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-5 h-5 text-teal-600" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Start Working</h3>
                        <span className="text-babyblue-500 text-sm">(Arbeitsbeginn)</span>
                      </div>
                      <p className="text-babyblue-600">Once you have your B2 certificate and recognition, you can work as a licensed healthcare professional!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Nurse Communication & Vocabulary */}
          <section className="py-12 px-6 bg-gradient-to-b from-teal-50 to-white">
            <div className="container mx-auto max-w-5xl">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
                  Learn Nursing German
                </h2>
                <p className="text-babyblue-600">Essential phrases for caring for patients and elders</p>
              </div>

              {/* Nurse Phrases */}
              <div className="bg-white rounded-2xl border border-teal-200 p-6 mb-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-babyblue-800">Common Nursing Phrases</h3>
                </div>

                <div className="space-y-2">
                  {nursePhrases.map((phrase, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer" onClick={() => speakGerman(phrase.de, `nurse-${index}`)} data-testid={`phrase-row-nurse-${index}`}>
                      <div className="flex-1">
                        <p className="font-medium text-babyblue-800">{phrase.de}</p>
                        <p className="text-babyblue-500 text-sm">{phrase.en}</p>
                      </div>
                      <button
                        type="button"
                        className={`p-2 rounded-full transition-all ${
                          playingAudio === `nurse-${index}` 
                            ? 'bg-teal-500 text-white' 
                            : 'bg-white text-teal-500 hover:bg-teal-500 hover:text-white'
                        }`}
                      >
                        <Volume2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ============== DOCTOR CONTENT ============== */}
      {careerPath === 'doctor' && (
        <>
          {/* Doctor Steps */}
          <section className="py-12 px-6 bg-white">
            <div className="container mx-auto max-w-4xl">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-8 text-center">
                Steps to Work as a Doctor in Germany
              </h2>

              <div className="space-y-4">
                {/* Step 1: Language */}
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Languages className="w-5 h-5 text-primary" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Language Level</h3>
                        <span className="text-babyblue-500 text-sm">(Sprachniveau)</span>
                      </div>
                      <p className="text-babyblue-600 mb-3">You need <strong>B2 level</strong> for the medical language exam and <strong>C1</strong> is recommended for practice.</p>
                    </div>
                  </div>
                </div>

                {/* Step 2: FSP Exam */}
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <GraduationCap className="w-5 h-5 text-primary" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">FSP-Prüfung</h3>
                        <span className="text-babyblue-500 text-sm">(Fachsprachprüfung)</span>
                      </div>
                      <p className="text-babyblue-600 mb-3">The Medical Language Exam consists of three parts:</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="bg-white p-3 rounded-lg text-center border border-blue-200">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-1">
                            <span className="text-primary font-bold text-sm">1</span>
                          </div>
                          <p className="text-babyblue-700 text-sm">Patient-Doctor Conversation</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg text-center border border-blue-200">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-1">
                            <span className="text-primary font-bold text-sm">2</span>
                          </div>
                          <p className="text-babyblue-700 text-sm">Written Documentation</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg text-center border border-blue-200">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-1">
                            <span className="text-primary font-bold text-sm">3</span>
                          </div>
                          <p className="text-babyblue-700 text-sm">Doctor-to-Doctor Exchange</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Berufserlaubnis */}
                <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileCheck className="w-5 h-5 text-green-600" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Berufserlaubnis</h3>
                        <span className="text-babyblue-500 text-sm">(Temporary Work Permit - §10 BÄO)</span>
                      </div>
                      <p className="text-babyblue-600 mb-3">After passing FSP, you can apply for a temporary work permit to work <strong>under supervision</strong>.</p>
                      <div className="bg-white p-3 rounded-lg border border-green-200">
                        <p className="text-green-800 text-sm">✓ Earn money while preparing for Kenntnisprüfung</p>
                        <p className="text-green-800 text-sm">✓ Gain practical experience in German hospitals</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 4: Approbation */}
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0">4</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-primary" />
                        <h3 className="font-heading text-lg font-bold text-babyblue-800">Approbation</h3>
                        <span className="text-babyblue-500 text-sm">(Full Medical License)</span>
                      </div>
                      <p className="text-babyblue-600">Complete recognition process and submit required documents to receive your full license.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Legal Requirements for Doctors */}
          <section className="py-12 px-6 bg-gradient-to-b from-blue-50 to-white">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Scale className="w-4 h-4" />
                  Important Knowledge
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
                  Legal Requirements for Doctors
                </h2>
                <p className="text-babyblue-600">You must understand these German medical laws</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Rechtsmedizin */}
                <div className="bg-white rounded-xl p-5 border border-red-200 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <Scale className="w-6 h-6 text-red-500" />
                    <div>
                      <h3 className="font-bold text-babyblue-800">Rechtsmedizin</h3>
                      <p className="text-red-500 text-xs">Forensic Medicine</p>
                    </div>
                  </div>
                  <ul className="text-babyblue-600 text-sm space-y-1">
                    <li>• Pronouncing death criteria</li>
                    <li>• Death certificates (Todesbescheinigung)</li>
                    <li>• Non-accidental injury signs</li>
                  </ul>
                </div>

                {/* Betäubungsmittelgesetz */}
                <div className="bg-white rounded-xl p-5 border border-amber-200 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <Pill className="w-6 h-6 text-amber-500" />
                    <div>
                      <h3 className="font-bold text-babyblue-800">Betäubungsmittelgesetz</h3>
                      <p className="text-amber-500 text-xs">German Narcotics Act</p>
                    </div>
                  </div>
                  <p className="text-babyblue-600 text-sm">Strict laws governing prescription of painkillers and controlled medications.</p>
                </div>

                {/* Patientenrechte */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <ShieldCheck className="w-6 h-6 text-blue-500" />
                    <div>
                      <h3 className="font-bold text-babyblue-800">Patientenrechte</h3>
                      <p className="text-blue-500 text-xs">Patient Rights</p>
                    </div>
                  </div>
                  <ul className="text-babyblue-600 text-sm space-y-1">
                    <li>• Informed Consent</li>
                    <li>• Schweigepflicht (Confidentiality)</li>
                    <li>• Patientenverfügung (Directives)</li>
                  </ul>
                </div>

                {/* Sozialversicherung */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-3">
                    <Building2 className="w-6 h-6 text-blue-500" />
                    <div>
                      <h3 className="font-bold text-babyblue-800">Sozialversicherung</h3>
                      <p className="text-blue-500 text-xs">Insurance System</p>
                    </div>
                  </div>
                  <p className="text-babyblue-600 text-sm">Understand GKV (statutory) vs PKV (private) insurance systems.</p>
                  <Link to="/integration/healthcare-system" className="text-primary text-sm font-medium mt-2 inline-block hover:underline">
                    Learn more →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Final Documents */}
          <section className="py-12 px-6 bg-white">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-2">
                  Final Approbation Documents
                </h2>
                <p className="text-babyblue-600 text-sm">Required documents (not older than 3 months)</p>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-blue-50 rounded-2xl p-6 border border-primary/20">
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-babyblue-800">Ärztliche Bescheinigung</h4>
                      <p className="text-babyblue-600 text-sm">Medical certificate proving physical and mental capability</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-babyblue-800">Certificate of Good Standing</h4>
                      <p className="text-babyblue-600 text-sm">Proof license hasn't been suspended for malpractice</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-babyblue-800">Polizeiliches Führungszeugnis</h4>
                      <p className="text-babyblue-600 text-sm">Clean criminal record from German authorities</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Doctor Communication */}
          <section className="py-12 px-6 bg-gradient-to-b from-blue-50 to-white">
            <div className="container mx-auto max-w-5xl">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
                  Learn Medical German
                </h2>
                <p className="text-babyblue-600">Essential phrases for doctor-patient communication</p>
              </div>

              {/* Doctor Phrases */}
              <div className="bg-white rounded-2xl border border-blue-200 p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-babyblue-800">Doctor-Patient Phrases</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                  {doctorPhrases.map((phrase, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer" onClick={() => speakGerman(phrase.de, `doctor-${index}`)} data-testid={`phrase-row-doctor-${index}`}>
                      <div className="flex-1">
                        <p className="font-medium text-babyblue-800 text-sm">{phrase.de}</p>
                        <p className="text-babyblue-500 text-xs">{phrase.en}</p>
                      </div>
                      <button
                        type="button"
                        className={`p-2 rounded-full transition-all ${
                          playingAudio === `doctor-${index}` 
                            ? 'bg-primary text-white' 
                            : 'bg-white text-primary hover:bg-primary hover:text-white'
                        }`}
                      >
                        <Volume2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ============== SHARED CONTENT ============== */}
      {/* Medical Vocabulary - Shared with Categories */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
              Medical Vocabulary
            </h2>
            <p className="text-babyblue-600">Over 100 essential terms for healthcare professionals</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {vocabCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveVocabCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeVocabCategory === cat.id
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-babyblue-100 text-babyblue-700 hover:bg-babyblue-200'
                }`}
              >
                <span>{cat.icon}</span>
                <span className="hidden sm:inline">{cat.name}</span>
              </button>
            ))}
          </div>

          {/* Vocabulary Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {medicalVocabulary
              .filter(word => word.category === activeVocabCategory)
              .map((word, index) => (
              <div 
                key={index} 
                className="bg-babyblue-50 rounded-xl p-4 border border-babyblue-200 flex items-center justify-between group hover:border-primary hover:shadow-md transition-all cursor-pointer"
                onClick={() => speakGerman(word.de, `vocab-${activeVocabCategory}-${index}`)}
              >
                <div>
                  <p className="font-semibold text-babyblue-800">{word.de}</p>
                  <p className="text-babyblue-500 text-sm">{word.en}</p>
                </div>
                <div className={`p-2 rounded-full transition-all ${
                  playingAudio === `vocab-${activeVocabCategory}-${index}` 
                    ? 'bg-primary text-white' 
                    : 'bg-white text-primary opacity-0 group-hover:opacity-100'
                }`}>
                  <Volume2 className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Body Parts - Shared */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50 to-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-babyblue-800 mb-2">
              Body Parts
            </h2>
            <p className="text-babyblue-600">Körperteile - Click to hear pronunciation</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {bodyParts.map((part, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-4 border border-babyblue-200 text-center group hover:border-primary hover:shadow-lg transition-all cursor-pointer"
                onClick={() => speakGerman(part.de, `body-${index}`)}
              >
                <div className={`text-4xl mb-2 transition-transform group-hover:scale-110 ${
                  playingAudio === `body-${index}` ? 'animate-bounce' : ''
                }`}>
                  {part.emoji}
                </div>
                <p className="font-bold text-babyblue-800 text-sm">{part.de}</p>
                <p className="text-babyblue-500 text-xs">{part.en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-12 px-6 ${careerPath === 'nurse' ? 'bg-gradient-to-r from-teal-500 to-teal-400' : 'bg-gradient-to-r from-primary to-blue-400'}`}>
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Continue Your Learning Journey
          </h2>
          <p className="text-white/90 mb-6">
            Explore more vocabulary and practice exercises for your healthcare career.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/vocabulary"
              className="bg-white text-babyblue-800 hover:bg-babyblue-50 px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all"
            >
              More Vocabulary
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/integration/healthcare-system"
              className="bg-white/20 text-white hover:bg-white/30 px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all border border-white/30"
            >
              Healthcare System Guide
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Other Career Paths */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6 text-center">
            Explore Other Career Paths
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: '💻', title: 'IT & Technology', path: '/careers/it' },
              { icon: '🔧', title: 'Engineering', path: '/careers/engineering' },
              { icon: '🍽️', title: 'Hospitality', path: '/careers/hospitality' },
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HealthcareCareerPage;
